import { PointTooltip, ResponsiveLine, Serie } from "@nivo/line";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

export type ModalContentProps = {
  data: Serie[];
  tooltip: PointTooltip;
  classes?: string;
  format?: (value: number) => string;
  onHover?: (pointer: { x: number; y: number }) => void;
  dataPointCount?: number;
};

/**
 * Calculate Y-axis tick values to ensure exactly 5 gridlines
 * based on the maximum value in the data
 */
export const calculateYAxisTicks = (maxValue: number): number[] => {
  if (maxValue === 0) return [0];

  // Calculate interval to create 5 gridlines (0, interval, 2*interval, 3*interval, 4*interval)
  const interval = maxValue / 4;

  // Round interval to a nice number
  const magnitude = Math.pow(10, Math.floor(Math.log10(interval)));
  const normalizedInterval = Math.ceil(interval / magnitude) * magnitude;

  return [
    0,
    normalizedInterval,
    2 * normalizedInterval,
    3 * normalizedInterval,
    4 * normalizedInterval,
  ];
};

/**
 * Get the maximum Y value from all data series
 */
export const getMaxDataValue = (data: Serie[]): number => {
  let maxValue = 0;

  data.forEach((series) => {
    series.data?.forEach((point) => {
      if (point.y !== null && typeof point.y === "number") {
        maxValue = Math.max(maxValue, point.y);
      }
    });
  });

  return maxValue;
};

/**
 * Check if the date range spans multiple years
 */
export const spansMultipleYears = (tickValues?: string[]): boolean => {
  if (!tickValues || tickValues.length < 2) return false;

  const years = tickValues.map((dateStr) => dayjs(dateStr).year());
  return new Set(years).size > 1;
};

export const LineChart = ({
  data,
  tooltip,
  classes,
  format,
  onHover,
  dataPointCount = 0,
}: ModalContentProps) => {
  // Track hovered data point for showing dot and vertical line
  const [hoveredX, setHoveredX] = useState<string | null>(null);

  // Enable points for sparse data (≤2 data points)
  const isSparseData = dataPointCount <= 2;

  // Calculate Y-axis ticks based on max value (ensures exactly 5 gridlines)
  const yAxisTicks = useMemo(() => {
    const maxValue = getMaxDataValue(data);
    return calculateYAxisTicks(maxValue);
  }, [data]);

  // Check if date range spans multiple years for formatting
  const multipleYears = useMemo(() => {
    return spansMultipleYears(data[0]?.tickValues);
  }, [data]);

  // Handle mouse enter to track hovered point and call onHover callback
  const handleMouseEnter = (pointer: { x: number; y: number }) => {
    // Find the closest data point to the hover position
    if (data[0]?.data) {
      const hoveredPoint = data[0].data[pointer.x] as
        | { x?: string }
        | undefined;
      if (hoveredPoint?.x) {
        setHoveredX(hoveredPoint.x);
      }
    }
    onHover?.(pointer);
  };

  // Handle mouse leave to clear hover state
  const handleMouseLeave = () => {
    setHoveredX(null);
  };

  // Build markers: sparse data gets permanent vertical lines, hover gets temporary one
  const chartMarkers = useMemo(() => {
    const baseMarkers =
      isSparseData && data[0]?.data
        ? data[0].data
            .filter((point) => point.x != null && point.y != null)
            .map((point) => ({
              axis: "x" as const,
              value: point.x!,
              lineStyle: {
                stroke: "rgba(107, 165, 252, 0.2)",
                strokeWidth: 1,
                strokeDasharray: "4 4",
              },
            }))
        : [];

    // Add hover vertical line if user is hovering (except for sparse data where lines are always shown)
    if (hoveredX && !isSparseData) {
      baseMarkers.push({
        axis: "x" as const,
        value: hoveredX,
        lineStyle: {
          stroke: "rgba(107, 165, 252, 0.2)",
          strokeWidth: 1,
          strokeDasharray: "4 4",
        },
      });
    }

    return baseMarkers;
  }, [isSparseData, hoveredX, data]);

  // Point rendering: show for sparse data always, or conditionally don't show on hover in normal data
  const enablePointsForSparseData = isSparseData;

  const renderBottomTick = ({
    x,
    y,
    value,
    tickIndex,
    textY,
  }: {
    x: number;
    y: number;
    value: string | number;
    tickIndex: number;
    textY: number;
  }) => {
    const allTickValues = data[0]?.tickValues as string[] | undefined;
    const totalTicks = allTickValues?.length ?? 0;
    const isFirst = tickIndex === 0;
    const isLast = totalTicks > 0 && tickIndex === totalTicks - 1;
    const anchor = isFirst ? "start" : isLast ? "end" : "middle";
    const dateFormat = multipleYears ? "DD/MM/YYYY" : "DD/MM";
    const label = dayjs(String(value)).format(dateFormat);
    return (
      <g transform={`translate(${x},${y})`} key={String(value)}>
        <text
          textAnchor={anchor}
          dominantBaseline="text-before-edge"
          y={textY}
          style={{
            fill: "rgba(3, 20, 46, 0.5)",
            fontSize: 10,
            fontFamily: "Roboto",
          }}
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <div
      data-testid="line-chart"
      className={`flex flex-grow flex-col ${classes}`}
    >
      <div
        className="flex h-[232px] flex-grow rounded-xl bg-white md:h-[248px]"
        onMouseLeave={handleMouseLeave}
      >
        <ResponsiveLine
          data={data}
          margin={{ top: 24, right: 24, bottom: 32, left: 40 }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: 0,
            max: yAxisTicks[yAxisTicks.length - 1],
            stacked: false,
            reverse: false,
          }}
          theme={{
            background: "white",
            axis: {
              ticks: {
                text: {
                  fill: "rgba(3, 20, 46, 0.5)",
                  fontSize: 10,
                  fontFamily: "Roboto",
                },
              },
            },
            grid: {
              line: {
                stroke: "rgba(3, 20, 46, 0.1)",
                strokeDasharray: "4 4",
              },
            },
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            legendOffset: 56,
            legendPosition: "middle",
            tickValues: data[0]?.tickValues,
            renderTick: renderBottomTick,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            legendOffset: -50,
            legendPosition: "middle",
            format,
            tickValues: yAxisTicks,
          }}
          tooltip={tooltip}
          onMouseEnter={handleMouseEnter}
          onClick={(event) => {
            if (data[0]?.data && event?.data?.x) {
              setHoveredX(event.data.x as string);
            }
            onHover?.(event);
          }}
          colors={{ datum: "color" }}
          enableGridX={false}
          enableGridY={true}
          gridYValues={yAxisTicks}
          lineWidth={2}
          enablePoints={enablePointsForSparseData}
          pointSize={isSparseData ? 8 : 0}
          pointColor="rgba(107, 165, 252, 1)"
          pointBorderWidth={2}
          pointBorderColor="white"
          useMesh={true}
          enableArea={true}
          areaOpacity={1}
          areaBlendMode="normal"
          markers={chartMarkers}
          defs={[
            {
              id: "gradientFill",
              type: "linearGradient",
              coordinates: [
                { x: 0, y: 0 },
                { x: 0, y: 1 },
              ],
              colors: [
                { offset: 0, color: "rgba(107, 165, 252, 0.4)" },
                { offset: 100, color: "rgba(107, 165, 252, 0)" },
              ],
            },
          ]}
          fill={[{ match: "*", id: "gradientFill" }]}
        />
      </div>
    </div>
  );
};
